import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useParams from 'core/hooks/useParams'
import { getResmgrRoleConfig, updateHostRole } from '../actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import { isNilOrEmpty } from 'utils/fp'
import Alert from 'core/components/Alert'
import Badge from 'core/elements/badge'
import Checkbox from 'core/elements/input/Checkbox'
import Tooltip from 'core/elements/tooltip'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'

const useStyles = makeStyles<Theme>((theme) => ({
  hostInfo: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: '12px 24px',
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  label: {
    fontWeight: 500,
    color: theme.palette.grey[700],
  },
  value: {
    color: theme.palette.grey[900],
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 8,
  },
  infoIcon: {
    color: theme.palette.grey[500],
    cursor: 'help',
    fontSize: 14,
  },
  warningText: {
    marginTop: 8,
    marginBottom: 16,
    color: theme.palette.grey[700],
    fontSize: 14,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  modeOption: {
    marginBottom: 12,
    padding: '12px 20px',
    border: '1px solid',
    borderColor: theme.palette.grey[300],
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  modeOptionSelected: {
    borderColor: '#0089C7',
    backgroundColor: '#F2F9FC',
  },
  modeOptionDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
    backgroundColor: theme.palette.grey[50],
  },
  modeContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  modeTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 600,
    color: theme.palette.grey[900],
    fontSize: 14,
    lineHeight: '16px',
  },
  modeDescription: {
    color: theme.palette.grey[700],
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
  },
  tooltip: {
    marginTop: 12,
    marginLeft: 28,
    padding: 12,
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    borderRadius: 4,
    fontSize: 12,
  },
  profilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginTop: 16,
  },
  profileCard: {
    padding: 16,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  profileCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor: `${theme.palette.primary.light}15`,
  },
  profileContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontWeight: 600,
    color: theme.palette.grey[900],
    fontSize: 14,
    marginBottom: 4,
  },
  profileSlices: {
    color: theme.palette.grey[600],
    fontSize: 14,
  },
  tooltipDisabled: {
    cursor: 'not-allowed',
    width: '200px',
  },
}))

interface Props {
  rows: any[]
  onClose: () => void
}

export default function ConfigureGpuHostModal({ rows: [host], onClose }: Props) {
  const classes = useStyles()

  const defaultParams = {
    profiles: [],
  }
  const [submitting, setSubmitting] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [errorLoadingConfig, setErrorLoadingConfig] = useState(false)
  const [error, setError] = useState(null)
  const { params, setParams, updateParams } = useParams(defaultParams)
  const [role, setRole] = useState(null)
  const [initiallyLoadedProfiles, setInitiallyLoadedProfiles] = useState<string[]>([])

  // Helper function to parse enabled_mdev_types
  // Backend returns [] for empty, or a string like "nvidia-456, nvidia-567" when set
  const parseEnabledMdevTypes = useCallback((enabledMdevTypes: any): string[] => {
    if (!enabledMdevTypes) return []

    // Handle empty array case
    if (Array.isArray(enabledMdevTypes) && enabledMdevTypes.length === 0) {
      return []
    }

    // Handle string case
    if (typeof enabledMdevTypes === 'string' && enabledMdevTypes.trim()) {
      return enabledMdevTypes
        .split(',')
        .map((name: string) => name.trim())
        .filter(Boolean)
    }

    return []
  }, [])

  // Load current configuration
  useEffect(() => {
    const getConfiguration = async () => {
      try {
        const response: any = await getResmgrRoleConfig({ host, role: 'pf9-ostackhost-neutron' })

        if (response) {
          setRole(response)

          // Parse existing vGPU profiles from config
          const profileNames = parseEnabledMdevTypes(response?.enabled_mdev_types)
          setInitiallyLoadedProfiles(profileNames)
          updateParams({ profiles: profileNames })
        }

        setLoadingConfig(false)
      } catch (e) {
        setErrorLoadingConfig(true)
      } finally {
        setLoadingConfig(false)
      }
    }
    getConfiguration()
  }, [host, updateParams])

  // Handle profile toggle
  const toggleProfile = useCallback(
    (profileName: string) => {
      const currentProfiles = params.profiles || []
      const isSelected = currentProfiles.includes(profileName)

      if (isSelected) {
        updateParams({ profiles: currentProfiles.filter((p) => p !== profileName) })
      } else {
        updateParams({ profiles: [...currentProfiles, profileName] })
      }
    },
    [params, updateParams],
  )

  // Submit form - handles vGPU profile configuration
  const submitForm = useCallback(async () => {
    setSubmitting(true)
    setError(null)

    try {
      // Validate at least one profile is selected
      if (params.profiles.length === 0) {
        setError({ message: 'Please select at least one vGPU profile' })
        setSubmitting(false)
        return
      }

      // Build mdev_config array
      // Example: [{"name": "mdev_nvidia-456", "max_instances": "10"}, {"name": "mdev_nvidia-567", "max_instances": "12"}]
      const mdevConfigArray = params.profiles.map((profileName) => {
        const profile = host?.allGpuProfiles?.find((p: any) => p.profileName === profileName)
        return {
          name: `mdev_${profileName}`,
          max_instances: String(profile?.['Max Instances'] || 1),
        }
      })

      const body = {
        ...role,
        enabled_mdev_types: params.profiles.join(', '),
        mdev_config: JSON.stringify(mdevConfigArray),
      }

      await updateHostRole({ hostId: host.id, role: 'pf9-ostackhost-neutron', body })

      handleClose()
    } catch (e) {
      setError({ message: 'Configuration failed to update' })
    } finally {
      setSubmitting(false)
    }
  }, [role, params, host])

  const handleClose = () => {
    setParams(defaultParams)
    setLoadingConfig(true)
    setErrorLoadingConfig(false)
    setError(null)
    onClose()
  }

  // Get the existing configured profile names for display
  const existingProfile = useMemo(() => {
    const profileNames = parseEnabledMdevTypes(role?.enabled_mdev_types)

    if (profileNames.length === 0) return null

    // Find display names for each profile
    const profiles = profileNames
      .map(
        (profileName) =>
          host?.allGpuProfiles?.find((profile) => profile.profileName === profileName)?.name,
      )
      .filter(Boolean)

    return profiles.length > 0 ? profiles.join(', ') : null
  }, [role, host, parseEnabledMdevTypes])

  // Memoize sorted profiles to avoid re-sorting on every render
  const sortedProfiles = useMemo(() => {
    if (!host?.allGpuProfiles) return []

    const initiallyLoadedSet = new Set(initiallyLoadedProfiles)

    return [...host.allGpuProfiles].sort((a: any, b: any) => {
      const aWasInitiallyLoaded = initiallyLoadedSet.has(a.profileName)
      const bWasInitiallyLoaded = initiallyLoadedSet.has(b.profileName)

      // 1. Initially loaded profiles come first
      if (aWasInitiallyLoaded !== bWasInitiallyLoaded) {
        return aWasInitiallyLoaded ? -1 : 1
      }

      const aHasAvailable = typeof a.Available === 'number'
      const bHasAvailable = typeof b.Available === 'number'

      // 2. Available profiles come before unavailable
      if (aHasAvailable && bHasAvailable) {
        const aIsAvailable = a.Available > 0
        const bIsAvailable = b.Available > 0

        if (aIsAvailable !== bIsAvailable) {
          return aIsAvailable ? -1 : 1
        }
      }

      // 3. Keep original order if same priority
      return 0
    })
  }, [host?.allGpuProfiles, initiallyLoadedProfiles])

  return (
    <ModalForm
      title="Configure GPU Host Model"
      onSubmit={submitForm}
      onClose={handleClose}
      loading={loadingConfig}
      submitting={submitting}
      error={error}
      submitTitle="Save Changes"
      disableSubmit={isNilOrEmpty(role) || submitting}
      info={<Badge variant="warning" text="Beta" />}
      open
    >
      {isNilOrEmpty(role) && (
        <>
          <Alert variant="error">Required GPU role not found</Alert>
          <br />
        </>
      )}

      {/* Host Information */}
      <div className={classes.hostInfo}>
        <Text variant="body2" className={classes.label}>
          Host Name:
        </Text>
        <Text variant="body2" className={classes.value}>
          {host.name}
        </Text>

        <Text variant="body2" className={classes.label}>
          GPU Model:
        </Text>
        <Text variant="body2" className={classes.value}>
          {host.gpuModels?.join(', ') || 'N/A'}
        </Text>

        <Text variant="body2" className={classes.label}>
          Device ID:
        </Text>
        <Text variant="body2" className={classes.value}>
          {host.deviceIds?.join(', ') || 'N/A'}
        </Text>

        <Text variant="body2" className={classes.label}>
          GPU Mode:
        </Text>
        <Text variant="body2" className={classes.value}>
          {host.gpuMode || 'N/A'}
        </Text>

        <Text variant="body2" className={classes.label}>
          vGPU Profile:
        </Text>
        <Text variant="body2" className={classes.value}>
          {existingProfile || 'N/A'}
        </Text>
      </div>

      {errorLoadingConfig && <ErrorMessage>Error loading configured profiles</ErrorMessage>}

      {/* Passthrough mode message */}
      {host.gpuMode === 'Passthrough' && (
        <div className={classes.section}>
          <Text variant="body2">No configurations required for passthrough GPU mode.</Text>
        </div>
      )}

      {/* vGPU Profiles Section - Only shown when host gpuMode is vGPU */}
      {host.gpuMode === 'vGPU' && (
        <div className={classes.section}>
          <div className={classes.sectionTitle}>
            <Text variant="subtitle2">Profiles</Text>
            <Tooltip message="Select one or more profiles that this host supports. These profiles define how the GPU resources are split for use in virtual machines. Note: Only profiles with available slices can be selected.">
              <FontAwesomeIcon className={classes.infoIcon}>question-circle</FontAwesomeIcon>
            </Tooltip>
          </div>

          <div className={classes.profilesGrid}>
            {sortedProfiles.map((profile: any) => {
              const isSelected = params.profiles.includes(profile.profileName)
              const memoryInGB = (profile.Memory?.replace('M', '') / 1024).toFixed(0)
              // Treat undefined as available (backward compatibility), only 0, null, or negative as unavailable
              const isAvailable = profile.Available === undefined || profile.Available > 0
              const wasInitiallyLoaded = initiallyLoadedProfiles.includes(profile.profileName)
              const isDisabled = !isAvailable && !wasInitiallyLoaded

              const cardContent = (
                <div
                  className={`${classes.profileCard} ${
                    isSelected ? classes.profileCardSelected : ''
                  } ${isDisabled ? classes.modeOptionDisabled : ''}`}
                  onClick={() => !isDisabled && toggleProfile(profile.profileName)}
                >
                  <div className={classes.profileContent}>
                    <Checkbox
                      id={profile.profileName}
                      checked={isSelected}
                      onChange={() => !isDisabled && toggleProfile(profile.profileName)}
                      label=""
                      disabled={isDisabled}
                    />
                    <div className={classes.profileInfo}>
                      <div className={classes.profileName}>
                        {profile.profileName}
                        {isDisabled && (
                          <FontAwesomeIcon style={{ marginLeft: 6, fontSize: 12, color: '#999' }}>
                            info-circle
                          </FontAwesomeIcon>
                        )}
                      </div>
                      <div className={classes.profileSlices}>
                        {profile['Max Instances']} slices ({memoryInGB}GB each)
                      </div>
                    </div>
                  </div>
                </div>
              )

              return isDisabled ? (
                <Tooltip
                  key={profile.profileName}
                  message="Insufficient vGPU resources on the host for this profile"
                  customClassName={classes.tooltipDisabled}
                  offset={{ vertical: -10, horizontal: 0 }}
                  align={{ vertical: 'top', horizontal: 'middle' }}
                >
                  {cardContent}
                </Tooltip>
              ) : (
                <React.Fragment key={profile.profileName}>{cardContent}</React.Fragment>
              )
            })}
          </div>
        </div>
      )}
    </ModalForm>
  )
}
